import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import DndItem from '@/src/components/atom/DndItem';
import LocalIcon from '@/src/components/atom/Icons/LocalIcon';
import Typo from '@/src/components/atom/Typo';
import ImagePreview from '@/src/components/molecule/ImagePreview';
import useDnd from '@/src/hooks/useDnd';
import { useToast } from '@/src/hooks/useToast';
import { useMutation } from '@tanstack/react-query';
import { GripVertical } from 'lucide-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TablePlaceholderSvg from '@/assets/icons/icon-table-placeholder.svg';
import { HomeContentDto } from '@/src/models/HomeContentDto';
import { putHomeContentsOrderAPI } from '@/src/apis/rest/homeContent';

interface OrderModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOkClick: () => void;
  items: HomeContentDto[];
}

export default function OrderModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOkClick,
  items: itemsProps,
}: OrderModalProps) {
  const { toast } = useToast();
  const { moveInsertCardHandler, items } = useDnd(itemsProps);

  // mutation
  const putHomeContentOrderNumberMutation = useMutation({
    mutationFn: putHomeContentsOrderAPI,
  });

  // event
  const handleCloseModal = () => {
    onOpenChangeProps(false);
  };
  const handleOkClick = () => {
    putHomeContentOrderNumberMutation.mutate(
      {
        modifyHomeContentOrderDto: items.map((item, idx) => ({
          exposeIndex: idx + 1,
          id: item.id,
        })),
      },
      {
        onSuccess: () => {
          toast({
            title: '성공',
            description: '성공적으로 저장되었습니다.',
            variant: 'success',
          });
          onOkClick();
          handleCloseModal();
        },
        onError: e => {
          toast({
            title: '실패',
            description: e.message,
            variant: 'error',
          });
        },
      },
    );
  };

  // render
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          취소
        </Button>
        <Button
          className="flex-1"
          onClick={handleOkClick}
          isLoading={putHomeContentOrderNumberMutation.isPending}
        >
          저장
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={openProps}
      onOpenChange={onOpenChangeProps}
      contentProps={{
        style: {
          width: 'calc(100vw - 24px)',
        },
        className: 'max-w-[967px]',
      }}
      title="노출 순번 관리"
      footer={renderFooter()}
    >
      <DndProvider backend={HTML5Backend}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-5 text-b14sb text-gray6">
            <p className="w-[80px] flex-shrink-0 flex-grow-0 pr-1 text-right">
              No.
            </p>
            <p className="flex-1">노출 목록</p>
          </div>
          <div className="flex flex-col gap-3">
            {items?.length ? (
              items.map((item, index) => (
                <DndItem
                  key={item.id}
                  index={index}
                  moveCardHandler={moveInsertCardHandler}
                  className="flex gap-2"
                >
                  <div className="flex flex-1 cursor-move items-center gap-5 rounded-xl border border-gray2">
                    <div className="flex flex-1 gap-5 p-3">
                      <GripVertical width={24} height={24} />
                      <p className="flex min-w-6 flex-shrink-0 items-center justify-center text-h16b">
                        {index + 1}
                      </p>
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <p className="text-b16r">{item.title}</p>
                      </div>
                    </div>
                    <ImagePreview
                      imgSrc={item.contentMedia.bufferFullUrl}
                      width={50}
                      height={50}
                    />
                  </div>
                </DndItem>
              ))
            ) : (
              <div className="flex min-h-[300px] flex-col items-center justify-center gap-2">
                <LocalIcon iconSrc={TablePlaceholderSvg} iconSize={56} />
                <Typo typoColor="gray2" typoType="b14r">
                  현재 노출중인 컨텐츠가 없습니다.
                </Typo>
              </div>
            )}
          </div>
        </div>
      </DndProvider>
    </Dialog>
  );
}
